import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebaseConfig';
import Header from '../Header';
import './UserManagement.css';

const UserManagement = () => {
  const { currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/game');
      return;
    }
    loadUsers();
  }, [isAdmin, navigate]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort by role (admins first) then by email
      usersData.sort((a, b) => {
        if (a.role === 'admin' && b.role !== 'admin') return -1;
        if (a.role !== 'admin' && b.role === 'admin') return 1;
        return (a.email || '').localeCompare(b.email || '');
      });
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Emails that are always admin and cannot be demoted.
  const PROTECTED_ADMIN_EMAILS = [
    'firangiz.aslanova@livingchess.net',
    'all_admins@livingchess.net',
  ];

  const handleToggleAdmin = async (user) => {
    // Prevent removing admin from protected admin accounts
    if (PROTECTED_ADMIN_EMAILS.includes((user.email || '').toLowerCase()) && user.role === 'admin') {
      alert('Cannot remove admin role from this protected admin account.');
      return;
    }

    const newRole = user.role === 'admin' ? 'user' : 'admin';
    const confirmMessage = newRole === 'admin'
      ? `Make ${user.email} an admin?`
      : `Remove admin role from ${user.email}?`;

    if (!window.confirm(confirmMessage)) return;

    try {
      setUpdating(user.id);
      const userRef = doc(db, 'users', user.id);
      await updateDoc(userRef, { role: newRole });

      // Update local state
      setUsers(users.map(u =>
        u.id === user.id ? { ...u, role: newRole } : u
      ));
    } catch (error) {
      console.error('Error updating user role:', error);
      alert(`Failed to update user role: ${error.message}`);
    } finally {
      setUpdating(null);
    }
  };

  const handleDeleteUser = async (user) => {
    // Guard protected admins and self-deletion on the client (the Cloud Function
    // enforces these too — this is just for a friendlier UX).
    if (PROTECTED_ADMIN_EMAILS.includes((user.email || '').toLowerCase())) {
      alert('This protected admin account cannot be deleted.');
      return;
    }
    if (user.email === currentUser?.email) {
      alert('You cannot delete your own account from here.');
      return;
    }

    if (!window.confirm(
      `Permanently delete ${user.email}?\n\nThis removes their login account and profile and cannot be undone.`
    )) return;

    try {
      setDeleting(user.id);
      const deleteUserFn = httpsCallable(functions, 'deleteUser');
      await deleteUserFn({ uid: user.id });

      // Remove from local state
      setUsers(users.filter(u => u.id !== user.id));
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(`Failed to delete user: ${error.message}`);
    } finally {
      setDeleting(null);
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="user-management-container">
      <Header />

      <div className="user-management-content">
        <div className="content-header">
          <div className="header-left">
            <h2 className="section-title">User Management</h2>
            <p className="section-subtitle">{users.length} registered users</p>
          </div>
          <button onClick={() => navigate('/admin')} className="back-button">
            Back to Dashboard
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Display Name</th>
                  <th>Role</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} className={user.role === 'admin' ? 'admin-row' : ''}>
                    <td className="email-cell">
                      {user.email}
                      {user.email === currentUser?.email && (
                        <span className="you-badge">You</span>
                      )}
                    </td>
                    <td>{user.displayName || '-'}</td>
                    <td>
                      <span className={`role-badge ${user.role === 'admin' ? 'role-admin' : 'role-user'}`}>
                        {user.role === 'admin' ? 'Admin' : 'User'}
                      </span>
                    </td>
                    <td className="date-cell">
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString()
                        : '-'
                      }
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleToggleAdmin(user)}
                          disabled={updating === user.id || deleting === user.id || PROTECTED_ADMIN_EMAILS.includes((user.email || '').toLowerCase())}
                          className={`toggle-role-button ${user.role === 'admin' ? 'remove-admin' : 'make-admin'}`}
                        >
                          {updating === user.id
                            ? 'Updating...'
                            : user.role === 'admin'
                              ? 'Remove Admin'
                              : 'Make Admin'
                          }
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          disabled={
                            updating === user.id ||
                            deleting === user.id ||
                            user.email === currentUser?.email ||
                            PROTECTED_ADMIN_EMAILS.includes((user.email || '').toLowerCase())
                          }
                          className="delete-user-button"
                        >
                          {deleting === user.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
